import { useCallback, useMemo } from 'react';

import type { EstimationUnit } from '@/api/schema/data-contracts';
import { EstimationUnitName } from '@/components/atoms/EstimationUnitName';
import { Stack } from '@/components/atoms/Stack';
import { useMessages } from '@/hooks/messages';
import type { OmitSafely } from '@/types';
import { sortExpressions } from '@/utils/sorting';

import type { EstimationItemsTableProps } from '../EstimationItemsTable';
import EstimationItemsTable from '../EstimationItemsTable';
import { estimationItemsGroupedTablesMessageDefinitions } from './messages';

export type EstimationItemsGroupedTableProps = OmitSafely<EstimationItemsTableProps, 'group'> & {
  estimationUnit: EstimationUnit;
};

const EstimationItemsGroupedTables: React.FC<EstimationItemsGroupedTableProps> = ({
  estimationItems,
  estimationModel,
  estimationUnit,
  showEstimateColumn = false,
  ...rest
}: EstimationItemsGroupedTableProps) => {
  const messages = useMessages(estimationItemsGroupedTablesMessageDefinitions);

  const { formModelEstimationItems } = rest;

  const estimationItemsByGroupId = useMemo(() => {
    if (!formModelEstimationItems) {
      return [];
    }
    return Object.groupBy(formModelEstimationItems, (i) => i.groupId);
  }, [formModelEstimationItems]);

  const getEstimationModelItemGroup = useCallback(
    (estimationModelGroupId: string) =>
      estimationModel.itemGroups.find((x) => x.id === estimationModelGroupId),
    [estimationModel],
  );

  if (Object.keys(estimationItemsByGroupId).length === 0) {
    return (
      <>
        <p className='mt-4'>
          {messages.estimationUnit()}: <EstimationUnitName estimationUnit={estimationUnit} />
        </p>
        <div className='mt-4 w-full overflow-x-auto rounded-xl' key='empty'>
          <EstimationItemsTable
            showEstimateColumn={showEstimateColumn}
            estimationItems={[]}
            estimationModel={estimationModel}
            {...rest}
          />
        </div>
      </>
    );
  }

  return (
    <div>
      <p className='mt-4'>
        {messages.estimationUnit()}: <EstimationUnitName estimationUnit={estimationUnit} />
      </p>
      <Stack gap={10}>
        {Object.keys(estimationItemsByGroupId)
          .map(
            (estimationModelItemGroupId) =>
              getEstimationModelItemGroup(estimationModelItemGroupId)!,
          )
          .sort(sortExpressions.byOrder)
          .map((estimationModelGroup) => (
            <div className='mt-4 w-full overflow-x-auto rounded-xl' key={estimationModelGroup.id}>
              <EstimationItemsTable
                group={estimationModelGroup}
                estimationItems={estimationItems.filter(
                  (x) => x.estimationModelGroupId === estimationModelGroup.id,
                )}
                estimationModel={estimationModel}
                showEstimateColumn={showEstimateColumn}
                {...rest}
              />
            </div>
          ))}
      </Stack>
    </div>
  );
};

export default EstimationItemsGroupedTables;
